import { Injectable } from '@angular/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

@Injectable({ providedIn: 'root' })
export class HapticService {
  private audioCtx: AudioContext | null = null;

  // ── Audio Initialization ──
  private getAudioContext(): AudioContext | null {
    if (this.audioCtx) return this.audioCtx;
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      this.audioCtx = new Ctx();
      return this.audioCtx;
    } catch {
      return null;
    }
  }

  // ── Haptic triggers ──
  private vibrate(pattern: number | number[]): void {
    try {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    } catch {}
  }

  private async applyHaptic(style: 'light' | 'success' | 'error'): Promise<void> {
    try {
      if (style === 'light') {
        await Haptics.impact({ style: ImpactStyle.Light });
      } else if (style === 'success') {
        await Haptics.notification({ type: NotificationType.Success });
      } else {
        await Haptics.notification({ type: NotificationType.Error });
      }
    } catch {
      // Fallback to generic vibration when Capacitor Haptics are unavailable
      if (style === 'light') {
        this.vibrate(10);
      } else if (style === 'success') {
        this.vibrate([30, 50, 30]);
      } else {
        this.vibrate([50, 100, 50, 100, 50]);
      }
    }
  }

  // ── Web Audio synthesis helper ──
  private scheduleTone(
    freq: number,
    duration: number,
    gain: number = 0.15,
    type: OscillatorType = 'sine',
    startOffset: number = 0
  ): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') {
        ctx.resume(); // fire‑and‑forget; if blocked by browser policy the tone simply won't sound
      }
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, now + startOffset);
      gainNode.gain.setValueAtTime(gain, now + startOffset);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.start(now + startOffset);
      osc.stop(now + startOffset + duration + 0.01);
    } catch {
      // Audio failure must never break the app
    }
  }

  // ── New sensory feedback methods ──

  /** A light ping that builds anticipation (e.g. intent hit). */
  triggerAnticipation(): void {
    try {
      this.scheduleTone(1200, 0.08, 0.2, 'sine');
      this.applyHaptic('light').catch(() => {});
    } catch {}
  }

  /** A rich chord and haptic burst signalling a reward / best deal. */
  triggerReward(): void {
    try {
      // C‑major chord (C4, E4, G4)
      this.scheduleTone(261.63, 0.3, 0.15, 'triangle');
      this.scheduleTone(329.63, 0.3, 0.15, 'triangle', 0.01);
      this.scheduleTone(392.00, 0.3, 0.15, 'triangle', 0.02);
      this.applyHaptic('success').catch(() => {});
    } catch {}
  }

  /** An urgent double‑beep + error vibration for alerts / failures. */
  triggerAlert(): void {
    try {
      this.scheduleTone(800, 0.1, 0.2, 'square');
      this.scheduleTone(800, 0.1, 0.2, 'square', 0.15);
      this.applyHaptic('error').catch(() => {});
    } catch {}
  }

  // ── Legacy haptic‑only methods (kept for backward compatibility) ──

  async tap(): Promise<void> {
    try {
      await this.applyHaptic('light');
    } catch {}
  }

  async success(): Promise<void> {
    try {
      await this.applyHaptic('success');
    } catch {}
  }

  async error(): Promise<void> {
    try {
      await this.applyHaptic('error');
    } catch {}
  }
}

export { HapticService as SensoryFeedbackService };
